import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  knowledgebaseCreateSchema,
  knowledgebaseUpdateSchema,
  type KnowledgebaseCreateInput,
  type KnowledgebaseUpdateInput,
} from '@ventin/shared';
import { useArticle, useCreateArticle, useUpdateArticle } from '@/lib/adminApi';

interface FieldErrors {
  title?: string;
  slug?: string;
  body?: string;
  tags?: string;
  form?: string;
}

/**
 * ArticleEditorPage — /admin/knowledgebase/new and /admin/knowledgebase/:slug/edit.
 *
 * Why a full page instead of a dialog: article bodies are long. The Plan
 * 02-02 schema allows arbitrary-length body text; a dialog shrinks the
 * textarea below the comfort threshold for real writing.
 *
 * Modes:
 *   - :slug undefined (i.e. /new route) → create mode
 *   - :slug present                      → edit mode (fetches via useArticle)
 *
 * Tags input: comma-separated text field. We don't render a chip component —
 * adding one mid-phase is out of scope per Phase-2 UI convention (no new
 * shadcn primitives). Helper copy: "Separate tags with commas" is pinned in
 * the test.
 *
 * Duplicate-slug handling mirrors CompanyFormDialog (Plan 02-09): regex the
 * API error message and surface inline on the slug field.
 */
export default function ArticleEditorPage() {
  const navigate = useNavigate();
  const { slug: routeSlug } = useParams<{ slug: string }>();
  const isEdit = routeSlug !== undefined;

  const { data: articleData, isLoading: isLoadingArticle } = useArticle(
    isEdit ? routeSlug : undefined,
  );
  const existing = articleData?.article;

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [tagsText, setTagsText] = useState('');
  const [body, setBody] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [busy, setBusy] = useState(false);

  const createMut = useCreateArticle();
  const updateMut = useUpdateArticle();

  // Sync state once the article loads in edit mode.
  useEffect(() => {
    if (!isEdit) return;
    if (existing) {
      setTitle(existing.title);
      setSlug(existing.slug);
      setTagsText(existing.tags.join(', '));
      setBody(existing.body);
    }
  }, [isEdit, existing]);

  /** Parse comma-separated tag text into a normalized `string[]`. */
  const parsedTags = useMemo(
    () =>
      tagsText
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t.length > 0),
    [tagsText],
  );

  /**
   * Build a payload and validate it against the appropriate schema. Returns
   * either a parsed input + empty errors, or a field-keyed error map.
   *
   * We validate against the create schema for the Save-button-enabled check
   * even in edit mode so the user gets the same visual affordance.
   */
  function buildPayload(): {
    input: KnowledgebaseCreateInput | KnowledgebaseUpdateInput | null;
    errs: FieldErrors;
  } {
    const raw = { title, slug, body, tags: parsedTags };
    const schema = isEdit ? knowledgebaseUpdateSchema : knowledgebaseCreateSchema;
    const parsed = schema.safeParse(raw);
    if (parsed.success) {
      return { input: parsed.data, errs: {} };
    }
    const errs: FieldErrors = {};
    for (const issue of parsed.error.issues) {
      const key = issue.path[0] as keyof FieldErrors | undefined;
      if (key && !errs[key]) errs[key] = issue.message;
    }
    return { input: null, errs };
  }

  // Save button is disabled whenever the current form values do not parse
  // cleanly against the create schema (same shape for both modes, since
  // edit mode always sends all fields).
  const isValid = useMemo(() => {
    const raw = { title, slug, body, tags: parsedTags };
    return knowledgebaseCreateSchema.safeParse(raw).success;
  }, [title, slug, body, parsedTags]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const { input, errs } = buildPayload();
    if (input === null) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      if (isEdit) {
        if (!existing) throw new Error('Article not loaded');
        await updateMut.mutateAsync({
          id: existing.id,
          patch: input as KnowledgebaseUpdateInput,
        });
      } else {
        await createMut.mutateAsync(input as KnowledgebaseCreateInput);
      }
      toast.success('Saved');
      navigate('/admin/knowledgebase');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (/slug/i.test(message) && /(conflict|already|409|unique)/i.test(message)) {
        setErrors({ slug: 'Slug already in use' });
      } else {
        setErrors({ form: "Couldn't save — try again" });
        toast.error("Couldn't save — try again");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <header className="mb-6">
        <h2 className="text-2xl font-semibold leading-[1.3]">
          {isEdit ? 'Edit Article' : 'New Article'}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {isEdit
            ? 'Update the article content. Slug changes propagate immediately.'
            : 'Create a new knowledgebase article. Slug is used in URLs and cannot contain spaces.'}
        </p>
      </header>

      {isEdit && isLoadingArticle && (
        <p className="text-sm text-muted-foreground">Loading article…</p>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            aria-invalid={!!errors.title}
          />
          {errors.title && <p className="text-xs text-destructive">{errors.title}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="slug">Slug</Label>
          <Input
            id="slug"
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            aria-invalid={!!errors.slug}
          />
          {errors.slug && <p className="text-xs text-destructive">{errors.slug}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="tags">Tags</Label>
          <Input
            id="tags"
            type="text"
            value={tagsText}
            onChange={(e) => setTagsText(e.target.value)}
            placeholder="billing, onboarding"
            aria-invalid={!!errors.tags}
          />
          <p className="text-xs text-muted-foreground">Separate tags with commas</p>
          {errors.tags && <p className="text-xs text-destructive">{errors.tags}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="body">Body</Label>
          <textarea
            id="body"
            rows={20}
            className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            aria-invalid={!!errors.body}
          />
          {errors.body && <p className="text-xs text-destructive">{errors.body}</p>}
        </div>

        {errors.form && (
          <Alert variant="destructive">
            <AlertDescription>{errors.form}</AlertDescription>
          </Alert>
        )}

        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => navigate('/admin/knowledgebase')}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy || !isValid}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </div>
  );
}
